import React, { PureComponent, useState } from 'react';
import PropTypes from 'prop-types';
import {
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  Line,
  ReferenceArea,
  ReferenceLine,
  Area,
  ComposedChart,
} from 'recharts';
import _ from 'lodash';
import moment from 'moment-timezone';

import {
  //  CustomizedXAxisTickIrregularMinMaxOnly,
  CustomizedYAxisTickNumeric,
} from './GraphAxes';
import {
  DATE_FORMAT_YEAR_MONTH_DAY,
  DATE_FORMAT_MONTH_YEAR,
  DATE_FORMAT_MONTH_NAME,
  DATE_MONTH_DAY_YEAR_WITH_DASH,
} from '../../constants';
import {
  CONDENSED_VIEW_THRESHOLD,
  FIRST_IN_MONTH_MARKER,
  FIRST_IN_DAY_MARKER,
  GRAPH_ROW_BK,
  GRAPH_ROW_BK_ALTERNATE,
  GRAPH_DOT_RADIUS,
  GRAPH_DOT_RADIUS_CONDENSED,
  GRAPH_ACTIVE_DOT_RADIUS,
  GRAPH_AXIS_X_HEIGHT,
  GRAPH_AXIS_X_HEIGHT_CONDENSED,
  GRAPH_AXIS_LINE_COLOR,
  GRAPH_AXIS_LINE_WIDTH,
  GRAPH_REF_LINE_COLOR,
  GRAPH_REF_LINE_DASH,
  GRAPH_AXIS_FONT_SIZE,
  GRAPH_X_AXIS_FONT_COLOR,
  GRAPH_X_AXIS_FONT_COLOR_REPEATED,
} from './GraphConstants';

import './graph.scss';
import Strings from '../../Strings';

// const GRAPH_SLICES_BK = "#fff";

export const calcXAxisParams = (min, max, dateMinDay, dateMaxDay, xAxisLabelsMinMaxOnly) => {
    if (xAxisLabelsMinMaxOnly) {
      return {
        unit: 'day',
        drawFirstValue: true,
        drawLastValue: true,
        min: dateMinDay.unix(),
        max: dateMaxDay.unix(),
        ticks: [dateMinDay.unix(), dateMaxDay.unix()],
      };
    }

    const diffDays = dateMaxDay.diff(dateMinDay, 'days');
    const unit = diffDays <= 30 ? 'day' : diffDays <= 500 ? 'month' : 'year';
    const units = `${unit}s`;

    let dateMin;
    let dateMax;
    let diff;

    let drawFirstValue = false;
    let firstValue;
    let drawLastValue = false;
    let lastValue;

    switch (unit) {
      case 'year': {
        dateMin = dateMinDay
          .clone()
          .endOf('year')
          .add(1, 'years')
          .startOf('year');
        dateMax = dateMaxDay.clone().startOf('year');
        diff = dateMax.diff(dateMin, 'years');

        const diffFirstTickToLastTick = dateMax.diff(dateMin, 'days');
        const diffFirstValueToFirstTick = dateMin.diff(dateMinDay, 'days');
        const diffLastTickToLastValue = dateMaxDay.diff(dateMax, 'days');

        if (diffFirstValueToFirstTick > diffFirstTickToLastTick / 10) {
          drawFirstValue = true;
          firstValue = dateMinDay
            .clone()
            .endOf('month')
            .add(1, 'day')
            .startOf('month');
        }
        if (diffLastTickToLastValue > diffFirstTickToLastTick / 10) {
          drawLastValue = true;
          lastValue = dateMaxDay.clone().startOf('month');
        }

        break;
      }
      case 'month': {
        dateMin = dateMinDay
          .clone()
          .endOf('month')
          .add(1, 'months')
          .startOf('month');
        dateMax = dateMaxDay.clone().startOf('month');
        diff = dateMax.diff(dateMin, 'months');

        const diffFirstTickToLastTick = dateMax.diff(dateMin, 'days');
        const diffFirstValueToFirstTick = dateMin.diff(dateMinDay, 'days');
        const diffLastTickToLastValue = dateMaxDay.diff(dateMax, 'days');

        if (diffFirstValueToFirstTick > diffFirstTickToLastTick / 10) {
          drawFirstValue = true;
          firstValue = dateMinDay;
        }
        if (diffLastTickToLastValue > diffFirstTickToLastTick / 10) {
          drawLastValue = true;
          lastValue = dateMaxDay.clone().add(-1, 'days');
        }

        break;
      }
      case 'day':
      default: {
        dateMin = dateMinDay;
        dateMax = dateMaxDay;
        diff = diffDays;
        break;
      }
    }

    const ticks = Array.from({ length: diff + 1 }, (_, i) => i).map(i => dateMin.clone().add(i, units));
    if (drawFirstValue) ticks.splice(0, 0, firstValue);
    if (drawLastValue) ticks.push(lastValue);

    const ret = {
      unit,
      drawFirstValue,
      drawLastValue,
      min: dateMinDay.unix(),
      max: dateMaxDay.unix(),
      ticks: ticks.map(x => x.unix()),
    };

    return ret;
  };


const IsValidMoment = date => {
  return date != null && date instanceof moment && date.isValid();
};

class CustomizedXAxisProportional extends PureComponent {
  render() {
    const { x, y, verticalAnchor, payload, xAxisParams, xAxisLabelsMinMaxOnly } = this.props; // stroke

    const isTop = verticalAnchor === 'end';

    const day = moment(payload.value * 1000);
    const dayOfMonth = IsValidMoment(day) ? day.date() : -1;
    const monthOfYear = IsValidMoment(day) ? day.month() : -1;
    const month = IsValidMoment(day) ? day.format('MMM') : '';
    const year = IsValidMoment(day) ? day.format('YYYY') : '';

    const isFirstMonthOfYear = monthOfYear === 1;
    const isFirstDayOfMonth = dayOfMonth === 1;

    const isFirstTick = payload.value <= xAxisParams.ticks[0];
    const isLastTick = payload.value >= xAxisParams.ticks[xAxisParams.ticks.length - 1];

    const isExtraFirstTick = xAxisParams.drawFirstValue && isFirstTick;
    const isExtraLastTick = xAxisParams.drawLastValue && isLastTick;

    const unit = xAxisParams.unit;
    if (xAxisLabelsMinMaxOnly) {
      if (isFirstTick || isLastTick) {
        const value = day.format(DATE_FORMAT_MONTH_NAME);
        return (
          <g transform={`translate(${x},${y})`}>
            <text
              className="span"
              fontSize={GRAPH_AXIS_FONT_SIZE}
              dy={isTop ? -10 : 15}
              textAnchor={isFirstTick ? 'start' : 'end'}
              fill={GRAPH_X_AXIS_FONT_COLOR}
            >
              {value}
            </text>
          </g>
        );
      }
      return null;
    }
    let value1;
    let value2;

    switch (unit) {
      case 'day':
        value1 = dayOfMonth;
        value2 = isFirstDayOfMonth || isFirstTick ? `${month} ${year}` : '';
        break;
      case 'month':
        value1 = `${month}${isExtraFirstTick || isExtraLastTick ? `, ${dayOfMonth}` : ''}`;
        value2 = isFirstMonthOfYear || isFirstTick ? year : '';
        break;
      case 'year':
        value1 = !isExtraFirstTick ? year : `${month} ${year}`;
        value2 = '';
        break;
      default:
        break;
    }

    return (
      <g transform={`translate(${x},${y})`}>
        <text
          className="span"
          fontSize={GRAPH_AXIS_FONT_SIZE}
          dy={isTop ? -30 : 15}
          textAnchor={isExtraFirstTick || isExtraLastTick ? 'middle' : 'start'}
          fill={GRAPH_X_AXIS_FONT_COLOR}
        >
          {value1}
        </text>

        <text
          className="span"
          fontSize={GRAPH_AXIS_FONT_SIZE}
          dy={isTop ? -10 : 35}
          textAnchor={isExtraFirstTick || isExtraLastTick ? 'middle' : 'start'}
          fill={GRAPH_X_AXIS_FONT_COLOR_REPEATED}
        >
          {value2}
        </text>
      </g>
    );
  }
}
CustomizedXAxisProportional.propTypes = {
  x: PropTypes.number,
  y: PropTypes.number,
  verticalAnchor: PropTypes.string,
  payload: PropTypes.any,
  xAxisParams: PropTypes.object,
  extraFirstValue: PropTypes.number,
  extraLastValue: PropTypes.number,
  xAxisLabelsMinMaxOnly: PropTypes.bool,
};

export { CustomizedXAxisProportional };

function calcSliceWidth(diff, maxTicks) {
  const m = 60;
  const h = 60 * 60;
  const d = 24 * 60 * 60;
  const possibleTickSizes = [
    m,
    5 * m,
    15 * m,
    30 * m,
    h,
    2 * h,
    4 * h,
    6 * h,
    12 * h,
    d,
    7 * d,
    14 * d,
    30 * d,
    60 * d,
    120 * d,
    365 * d,
  ];

  for (let i = 0; i < possibleTickSizes.length; i += 1) {
    if (diff / possibleTickSizes[i] <= maxTicks) {
      return possibleTickSizes[i];
    }
  }

  return possibleTickSizes[possibleTickSizes / length - 1];
}

function GetSeries_Slices(data, xMin, xMax, maxTicks, yMin) {
  const diff = xMax - xMin;

  // less ticks if data is on little part of chart width
  // const dayS = 60 * 60 * 24;
  // let effectiveMaxTicks = diff >= dayS ? maxTicks: maxTicks * diff / dayS;
  // if(effectiveMaxTicks < 1) effectiveMaxTicks = 1;
  // effectiveMaxTicks = Math.round(effectiveMaxTicks);

  const effectiveMaxTicks = maxTicks;

  const sliceWidth = calcSliceWidth(diff, effectiveMaxTicks);
  const min = xMin % sliceWidth !== 0 ? xMin - (xMin % sliceWidth) : xMin;
  const max = xMax % sliceWidth !== 0 ? xMax - (xMax % sliceWidth) + sliceWidth : xMax;

  const ret = [];
  let x = min;

  ret.push({
    x: x - sliceWidth / 2,
    x1: x - sliceWidth,
    x2: x,
    values: [],
    y: yMin,
  });

  for (; x <= max; x += sliceWidth) {
    const newItem = {
      x: x + sliceWidth / 2,
      x1: x,
      x2: x + sliceWidth,
      values:
        x === min
          ? data.filter(d => d.x >= x && d.x <= x + sliceWidth)
          : data.filter(d => d.x > x && d.x <= x + sliceWidth),
      y: yMin,
    };
    if (newItem.values.length > 0) {
      newItem.values.sort((i1, i2) => i1.x - i2.x);
    }
    ret.push(newItem);
  }

  ret.push({
    x: x + sliceWidth / 2,
    x1: x,
    x2: x + sliceWidth,
    values: [],
    y: yMin,
  });

  return ret;
}

function GetSeries_All(data) {
  const ret = [];
  data.forEach(day => {
    ret.push({
      x: day.x,
      y: day.values[0],
      all: day,
    });
  });

  return ret;
}

function GetSeries(data, index) {
  const ret = [];
  data.forEach(day => {
    ret.push({
      x: day.x,
      y: day.values[index],
    });
  });

  return ret;
}

export const chartTypeEnum = Object.freeze({ line: 'line', area: 'area' });

function GenericLineGraphWithProportionalXAxisGraph({ graphType = chartTypeEnum.line, ...props }) {
  const [mousePos, setMousePos] = useState({ x: 0, y: 0 });

  const onMouseMove = e => {
    setMousePos({ x: e.screenX, y: e.screenY });
    e.preve;
  };

  const PrepareData = (graphData, dataSeries, dateSelector, yMin) => {
    const data = [];

    const constData = { yMin };

    let lastXday = '';
    let lastXmonth = '';

    graphData.forEach(item => {
      const date = dateSelector(item);
      const xDay = date.format(DATE_FORMAT_YEAR_MONTH_DAY);
      const isFirstDay = lastXday !== xDay;
      lastXday = xDay;

      const xMonth = date.format(DATE_FORMAT_MONTH_YEAR);
      const isFirstMonth = lastXmonth !== xMonth;
      lastXmonth = xMonth;

      const graphEntry = {
        x: date.unix(),
        xS: date.format('LLL') + (isFirstMonth ? FIRST_IN_MONTH_MARKER : isFirstDay ? FIRST_IN_DAY_MARKER : ''),
        moment: date,
        values: [],
        ...constData,
      };

      dataSeries.forEach(ds => {
        graphEntry.values.push(ds.selector(item));
      });

      data.push(graphEntry);
    });

    return data;
  };

  const calcValMinMax2 = (graphData, dataSelector) => {
    const items = graphData.map(dataSelector);
    return {
      min: Math.min(...items),
      max: Math.max(...items),
    };
  };
  const calcValMinMax = (graphData, dataSeries) => {
    let result;
    dataSeries.forEach(ds => {
      const localMinMax = calcValMinMax2(graphData, ds.selector);
      if (result === undefined) {
        result = localMinMax;
      } else {
        if (result.min > localMinMax.min) result.min = localMinMax.min;
        if (result.max < localMinMax.max) result.max = localMinMax.max;
      }
    });

    return result;
  };

  const calcTickFromDelta = valDelta => {
    const maxTicks = 12;
    const tick =
      valDelta < maxTicks
        ? 1
        : valDelta < maxTicks * 2
          ? 2
          : valDelta < maxTicks * 5
            ? 5
            : valDelta < maxTicks * 10
              ? 10
              : valDelta < maxTicks * 20
                ? 20
                : valDelta < maxTicks * 50
                  ? 50
                  : valDelta < maxTicks * 100
                    ? 100
                    : valDelta < maxTicks * 200
                      ? 200
                      : valDelta < maxTicks * 500
                        ? 500
                        : 1000;

    return tick;
  };

  const {
    graphData,
    hasBorder,
    forceCondensed,
    xAxisTopPosition,
    xAxisLabelsMinMaxOnly,
    yAxisWidth,
    yAxisUnit,
    tooltipParams,
    dataSeries,
    referenceLines,
    dateSelector,
    hasReferenceAreas,
  } = props;

  const valMinMax = calcValMinMax(graphData, dataSeries);
  const valDelta = valMinMax.max - valMinMax.min;
  const tick = calcTickFromDelta(valDelta);

  const yMin = valMinMax.min - (valMinMax.min % tick) - (valMinMax.min % tick < tick / 10 ? tick : 0);
  const yMax = valMinMax.max - (valMinMax.max % tick) + tick + (valMinMax.max % tick > (tick * 9) / 10 ? tick : 0);
  const yTicksCount = (yMax - yMin) / tick + 1;
  const yTicks = Array.from({ length: yTicksCount }, (_, i) => yMin + i * tick);

  const chartData = PrepareData(graphData, dataSeries, dateSelector, yMin);
  const xMin = chartData[0].x;
  const xMax = chartData[chartData.length - 1].x;
  const dateMinDay = moment(xMin * 1000).startOf('day');
  const dateMaxDay = moment(xMax * 1000)
    .add(1, 'days')
    .startOf('day');

  const slicesData = GetSeries_Slices(chartData, dateMinDay.unix(), dateMaxDay.unix(), 100, yMin);

  if (!tooltipParams.tooltipDateFormat) tooltipParams.tooltipDateFormat = DATE_MONTH_DAY_YEAR_WITH_DASH;

  const CustomTooltip = props => {
    const { active, payload } = props;
    if (active && payload && payload.length > 0) {
      const payloadP0 = payload.filter(p => p.name === 'P0');
      if (
        payloadP0.length > 0 &&
        payloadP0[0].payload &&
        payloadP0[0].payload.values &&
        payloadP0[0].payload.values.length > 0
      ) {
        const thePayload = payloadP0[0].payload;
        const count = thePayload.values.length;
        const values = thePayload.values;
        const theDate = values[0].moment;

        if (count === 1) {
          return (
            <div className="customTooltip">
              <p className="customTooltipTitle">{tooltipParams.tooltipTitle}</p>
              <div className="customTooltipDescrHeader left-align">
                {theDate.format(tooltipParams.tooltipDateFormat)}
              </div>
              {tooltipParams.tooltipContentLines &&
                tooltipParams.tooltipContentLines(values[0]).map((ttl, index) => (
                  <div className="customTooltipDescr left-align" key={`customTT_1a_${index}`}>
                    {ttl}
                  </div>
                ))}
              {!tooltipParams.tooltipContentLines &&
                dataSeries.map((ds, index) => (
                  <div className="customTooltipDescr left-align" key={`customTT_1b_${index}`}>
                    {ds.name}: {thePayload.values[index].values[0] + (yAxisUnit ? ` ${yAxisUnit}` : '')}
                  </div>
                ))}
            </div>
          );
        }

        const tooltipNormalEntryLinesCount = tooltipParams.tooltipNormalEntryLinesCount
          ? tooltipParams.tooltipNormalEntryLinesCount
          : 1;
        const LINES_LIMIT = 10;
        const maxCount = tooltipParams.tooltipContentLines
          ? Math.floor(LINES_LIMIT / (tooltipNormalEntryLinesCount + 1))
          : Math.floor(LINES_LIMIT / 2);
        const countVisible = Math.min(count, maxCount);
        const countNotVisible = count - countVisible;

        return (
          <div className="customTooltip">
            <p className="customTooltipTitle">{tooltipParams.tooltipTitle}</p>

            {values.map((v, indexV) =>
              indexV >= countVisible ? (
                <React.Fragment />
              ) : (
                <React.Fragment>
                  <div className="customTooltipDescrHeader left-align" key={`customTT_${indexV}`}>
                    {v.moment.format(tooltipParams.tooltipDateFormat)}
                  </div>
                  {tooltipParams.tooltipContentLines &&
                    tooltipParams.tooltipContentLines(v).map((ttl, index) => (
                      <div className="customTooltipDescr left-align" key={`customTT_2a_${index}`}>
                        {ttl}
                      </div>
                    ))}
                  {!tooltipParams.tooltipContentLines &&
                    dataSeries.map((ds, index) => (
                      <div className="customTooltipDescr left-align" key={`customTT_2b_${index}`}>
                        {ds.name}: {v.values[index] + (yAxisUnit ? ` ${yAxisUnit}` : '')}
                      </div>
                    ))}
                </React.Fragment>
              ),
            )}

            {countNotVisible > 0 && (
              <div className="customTooltipDescrHeader left-align">
                {Strings.formatString(Strings.andXmore, countNotVisible)}
              </div>
            )}
          </div>
        );

        // return (
        //   <div className="customTooltip">
        //     <p className="customTooltipTitle">{tooltipTitle}</p>
        //     <div className="customTooltipDescr">{payload[0].payload.all.moment.format(tooltipParams.tooltipDateFormat)}</div>
        //     {tooltipContentLines &&
        //       tooltipContentLines(payload[0].payload.all).map(ttl => (
        //         <div className="customTooltipDescr" key={`customTT_${ttl}`}>
        //           {ttl}
        //         </div>
        //       ))}
        //     {!tooltipContentLines &&
        //       dataSeries.map((ds, index) => (
        //         <div className="customTooltipDescr" key={`customTT_${ds.name}`}>
        //           {ds.name}: {payload[0].payload.all.values[index] + (yAxisUnit ? ' ' + yAxisUnit : '')}
        //         </div>
        //       ))}
        //   </div>
        // );
      }
    }
    return null;
  };

  const xAxisParams = calcXAxisParams(xMin, xMax, dateMinDay, dateMaxDay, xAxisLabelsMinMaxOnly);

  const drawXAxis = () => {
    return (
      <XAxis
        type="number"
        dataKey="x"
        allowDataOverflow
        height={
          chartData.length > CONDENSED_VIEW_THRESHOLD || forceCondensed || xAxisLabelsMinMaxOnly
            ? GRAPH_AXIS_X_HEIGHT_CONDENSED
            : GRAPH_AXIS_X_HEIGHT
        }
        domain={[xAxisParams.min, xAxisParams.max]}
        ticks={xAxisParams.ticks}
        interval={0}
        name="day"
        orientation={xAxisTopPosition ? 'top' : 'bottom'}
        tick={<CustomizedXAxisProportional xAxisParams={xAxisParams} xAxisLabelsMinMaxOnly={xAxisLabelsMinMaxOnly} />}
        padding={{
          left: 6,
          right: 6,
        }}
        axisLine={{
          stroke: GRAPH_AXIS_LINE_COLOR,
          strokeWidth: GRAPH_AXIS_LINE_WIDTH,
        }}
      />
    );
  };

  const drawYAxis = () => {
    return (
      <YAxis
        type="number"
        dataKey="y"
        name="yAxis"
        tick={<CustomizedYAxisTickNumeric yMin={yMin} yMax={yMax} width={yAxisWidth} tick={tick} unit={yAxisUnit} />}
        domain={[yMin, yMax]}
        ticks={yTicks}
        interval={0}
        tickSize={0}
        width={yAxisWidth}
        axisLine={{
          stroke: GRAPH_AXIS_LINE_COLOR,
          strokeWidth: GRAPH_AXIS_LINE_WIDTH,
        }}
        padding={{
          top: 2,
          bottom: xAxisTopPosition ? 5 : 1,
        }}
      />
    );
  };

  const drawTooltip = () => {
    return <Tooltip content={<CustomTooltip />} dataKey="y" allowEscapeViewBox={{ x: false, y: false }} />;
  };

  /* reference areas to mimic alternate rows */
  const drawAlternateRows = () => {
    return Array.from({ length: yTicksCount }, (_, i) => i).map(i => (
      <ReferenceArea
        x1={xMin}
        x2={xMax}
        y1={i === 0 ? yMin : yMin + tick / 2 + (i - 1) * tick}
        y2={yMin + tick / 2 + i * tick}
        stroke={i % 2 === 0 ? GRAPH_ROW_BK : GRAPH_ROW_BK_ALTERNATE}
        fill={i % 2 === 0 ? GRAPH_ROW_BK : GRAPH_ROW_BK_ALTERNATE}
        key={`refarea${i}`}
      />
    ));
  };

  const drawInvisibleAreaForTooltip = () => {
    return <Line tooltipType="none" name="P0" data={GetSeries_All(chartData)} dataKey="y" visibility="hidden" />;
  };

  const drawInvisibleSlicesForTooltip = () => {
    return slicesData.map(slice => (
      <ReferenceArea
        className={slice.values.length > 0 ? 'refAreaVisibleOnHover' : 'refAreaNotVisible'}
        x1={slice.x1}
        x2={slice.x2}
        stroke="#eee"
        fill="#eee"
        key={`refslice_${slice.x1}`}
      />
    ));
  };

  const drawInvisibleLineForTooltip = () => {
    return <Line tooltipType="none" name="P0" data={slicesData} dataKey="y" visibility="hidden" activeDot={false} />;
  };

  const drawLineSeries = () => {
    return props.dataSeries.map((ds, index) => (
      <Line
        key={`Line_${ds.name}`}
        type="linear"
        tooltipType="none"
        name={ds.name}
        stroke={ds.color}
        strokeWidth={1.5}
        //        data={GetSeries(chartData, index)}
        dataKey={`values[${index}]`}
        connectNulls
        dot={{
          r:
            chartData.length > CONDENSED_VIEW_THRESHOLD || forceCondensed
              ? GRAPH_DOT_RADIUS_CONDENSED
              : GRAPH_DOT_RADIUS,
        }}
        activeDot={false}
      />
    ));
  };

  const drawAreaSeries = () => {
    return props.dataSeries.map((ds, index) => (
      <Area
        key={`Area_${ds.name}`}
        type="monotone"
        tooltipType="none"
        name={ds.name}
        stroke={ds.color}
        strokeWidth={2}
        fill={ds.color}
        fillOpacity="10%"
        data={GetSeries(chartData, index)}
        dataKey="y"
        connectNulls
        dot={{
          r:
            chartData.length > CONDENSED_VIEW_THRESHOLD || forceCondensed
              ? GRAPH_DOT_RADIUS_CONDENSED
              : GRAPH_DOT_RADIUS,
          fill: 'white',
          fillOpacity: 1,
          stroke: ds.color,
        }}
        activeDot={{ r: GRAPH_ACTIVE_DOT_RADIUS }}
      />
    ));
  };

  const drawReferenceLines = () => {
    return referenceLines.map(rl => (
      <ReferenceLine key={rl} y={rl} label="" stroke={GRAPH_REF_LINE_COLOR} strokeDasharray={GRAPH_REF_LINE_DASH} />
    ));
  };

  const drawLineChart = () => {
    return (
      <ComposedChart
        data={chartData}
        defaultShowTooltip={false}
        margin={{
          top: 5,
          right: 10,
          bottom: 5,
          left: 10,
        }}

      // onMouseMove={onMouseMove}
      >
        {drawXAxis()}
        {drawYAxis()}
        {drawTooltip()}
        {hasReferenceAreas && drawAlternateRows()}

        <ReferenceArea fill="#fff" y1={yMin} y2={yMax} fillOpacity={1} />
        {/* line chart specific */}
        {drawLineSeries()}
        {drawInvisibleLineForTooltip()}
        {drawInvisibleSlicesForTooltip()}

        {referenceLines && drawReferenceLines()}
      </ComposedChart>
    );
  };

  const drawAreaChart = () => {
    return (
      <ComposedChart
        data={chartData}
        defaultShowTooltip={false}
        margin={{
          top: 5,
          right: 10,
          bottom: 5,
          left: 10,
        }}
      >
        {drawXAxis()}
        {drawYAxis()}
        {drawTooltip()}
        {hasReferenceAreas && drawAlternateRows()}

        {/* line chart specific */}
        {drawInvisibleAreaForTooltip()}
        {drawAreaSeries()}

        {referenceLines && drawReferenceLines()}
      </ComposedChart>
    );
  };

  React.MouseEventHandler;

  if (graphType === chartTypeEnum.line) {
    return (
      <div className={`graphDiv withPadding ${hasBorder ? 'withBorder' : ''}`}>
        <ResponsiveContainer width="100%" height="99%" debounce>
          {drawLineChart()}
        </ResponsiveContainer>
      </div>
    );
  }

  if (graphType === chartTypeEnum.area) {
    return (
      <div className={`graphDiv withPadding ${hasBorder ? 'withBorder' : ''}`}>
        <ResponsiveContainer width="100%" height="99%" debounce>
          {drawAreaChart()}
        </ResponsiveContainer>
      </div>
    );
  }
}

export default React.memo(GenericLineGraphWithProportionalXAxisGraph, (prevProps, nextProps) =>
  _.isEqual(prevProps.graphData, nextProps.graphData),
);

GenericLineGraphWithProportionalXAxisGraph.propTypes = {
  graphData: PropTypes.array,
  hasBorder: PropTypes.bool,
  forceCondensed: PropTypes.bool,
  xAxisTopPosition: PropTypes.bool,
  xAxisLabelsMinMaxOnly: PropTypes.bool,
  yAxisWidth: PropTypes.number,
  yAxisUnit: PropTypes.string,
  dataSeries: PropTypes.array,
  referenceLines: PropTypes.array,
  dateSelector: PropTypes.func,
  hasReferenceAreas: PropTypes.bool,
  graphType: PropTypes.string,

  tooltipParams: PropTypes.shape({
    tooltipTitle: PropTypes.string,
    tooltipContentLines: PropTypes.func,
    tooltipDateFormat: PropTypes.string,
    tooltipNormalEntryLinesCount: PropTypes.number,
  }),
};
